import React, { useState } from 'react'
import { connect } from 'dva'
import PropTypes from 'prop-types'
import SelectSearch from 'react-select-search'
import { FEATURE_COLOR, FEATURE_TYPES, ICON_LIST, WEIGHT_OPTIONS } from '../../../../../lib/constants'
import { findTypeIcon, findTypeLabel, findWeightIcon, findWeightLabel } from '../../../../../lib/utils'
import actions from './actions'
import Button from '../../../../Button'
import ChevronDown from '../../../../../assets/svg/Feather/ChevronDown'
import Color from 'color'
import ColorPicker from '../../../../Form/ColorPicker'
import SidebarFooter from '../../../SidebarFooter'
import styles from './EditStylesFooter.module.scss'
import typeIcons from '../../../../../assets/featureType'

function EditStylesFooter(props) {
	const [color, setColor] = useState(null)
	const [icon, setIcon] = useState(null)
	const [strokeWidth, setStrokeWidth] = useState(null)

	const { features, numLines, numPolygons, numTracks, numWaypoints } = props

	const typeOptions = ICON_LIST.map((icon) => {
		return {
			name: icon.key,
			value: icon.display,
			image: typeIcons[icon.display.replace(/ /g, '')],
		}
	})

	const styleOptionSelected = (key) => {
		return (eventData, selectedValue) => {
			let newValue = null

			switch (key) {
				case 'color':
					setColor(eventData.target.value)
					newValue = new Color(eventData.target.value).rgb().color
					newValue.push(1)
					break
				case 'icon':
					setIcon(selectedValue.name)
					newValue = selectedValue.name
					break
				case 'strokeWidth':
					setStrokeWidth(selectedValue.value)
					newValue = selectedValue.value
					break
			}

			for (let fi = 0; fi < features.length; fi++) {
        features[fi] = {
          ...features[fi],
					properties: {
            ...features[fi].properties,
						[key]: newValue,
					},
				}
      }
      props.updateMapFeatures(features)
		}
	}

	const saveStyleChanges = () => {
		if (!icon && !color && !strokeWidth) {
			props.cancelStyleChanges()
		} else {
			let properties = {}
			if (icon) {
				properties.icon = icon
			}
			if (color) {
				let colorArr = Object.values(FEATURE_COLOR).filter((item) => item.hex === color)
				if (colorArr.length === 1) {
					properties.color = colorArr[0].rgba
				}
			}
			if (strokeWidth) {
				properties.strokeWidth = strokeWidth
			}

			props.updateBulkFeatures(properties)
		}
	}

	const formatCount = (values, text) => {
		const sum = values.reduce((a, b) => a + b, 0)
		return sum + ' ' + (sum === 1 ? text : text + 's')
	}

	return (
		<SidebarFooter className={styles.footer}>
			{(numPolygons > 0 || numLines > 0 || numTracks > 0) && (
				<div className={styles.section}>
					<div className={styles.sectionTitle}>
						<span>Selections Line Weight</span>
						<span>
							{numPolygons > 0 && formatCount([numPolygons], 'area')}
							{numPolygons > 0 && numLines > 0 && ', '}
							{numLines > 0 && formatCount([numLines], 'line')}
							{numLines > 0 && numTracks > 0 && ', '}
							{
								// to account for areas and tracks, with 0 lines
								numPolygons > 0 && numLines < 0 && numTracks > 0 && ', '
							}
							{numTracks > 0 && formatCount([numTracks], 'track')}
						</span>
					</div>
					<SelectSearch
						options={WEIGHT_OPTIONS}
						placeholder='Weight'
						onChange={styleOptionSelected('strokeWidth')}
						renderValue={(valueProps) => {
							return (
								<div {...valueProps} className='select-search__value-container'>
									<div className='select-search__value-label'>
										<img src={findWeightIcon(strokeWidth)} className={styles.iconSelect} />
										<span>{findWeightLabel(strokeWidth)}</span>
									</div>
									<ChevronDown fill='#8e8e92' color='none' />
								</div>
							)
						}}
						renderOption={(optionsProps, optionData, optionSnapshot) => {
							return (
								<button {...optionsProps} className='select-search__option'>
									<img src={optionData.image} className={styles.iconSelect} />
									<span>{optionData.text}</span>
								</button>
							)
						}}
					/>
				</div>
			)}

			{numWaypoints > 0 && (
				<div className={styles.section}>
					<div className={styles.sectionTitle}>
						<span>Selections Item Type</span>
						<span>{formatCount([numWaypoints], 'waypoint')}</span>
					</div>
					<SelectSearch
						options={typeOptions}
						onChange={styleOptionSelected('icon')}
						renderValue={(valueProps) => {
							return (
								<div {...valueProps} className='select-search__value-container'>
									<div className='select-search__value-label'>
										<img src={findTypeIcon(icon)} className={styles.iconSelect} />
										<span>{findTypeLabel(icon)}</span>
									</div>
									<ChevronDown fill='#8e8e92' color='none' />
								</div>
							)
						}}
						renderOption={(optionsProps, optionData, optionSnapshot) => {
							return (
								<button {...optionsProps} className='select-search__option'>
									<img src={optionData.image} className={styles.iconSelect} />
									<span>{optionsProps.value}</span>
								</button>
							)
						}}
					/>
				</div>
			)}

			<div className={styles.section}>
				<div className={styles.sectionTitle}>
					<span>Selections Color</span>
					<span>{formatCount([numWaypoints, numLines, numTracks, numPolygons], 'item')}</span>
				</div>
				<ColorPicker colorOptions={Object.values(FEATURE_COLOR).map((color) => color.hex)} name='waypoint-color' value={color} onChange={styleOptionSelected('color')} />
			</div>

			<div className={styles.actionButtons}>
				<Button theme={Button.THEME.outlinePrimary} onClick={() => props.cancelStyleChanges()}>
					Cancel
				</Button>
				<Button theme={Button.THEME.solidPrimary} onClick={saveStyleChanges}>
					Save
				</Button>
			</div>
		</SidebarFooter>
	)
}

EditStylesFooter.propTypes = {
	features: PropTypes.array,
	numLines: PropTypes.number,
	numPolygons: PropTypes.number,
	numTracks: PropTypes.number,
	numWaypoints: PropTypes.number,
	// actions
	cancelStyleChanges: PropTypes.func,
	updateBulkFeatures: PropTypes.func,
	updateMapFeatures: PropTypes.func,
}

export default connect((model) => {
	const { featuresList, selectedFeatures } = model.features
	let features = []
	let numLines = 0
	let numPolygons = 0
	let numTracks = 0
	let numWaypoints = 0

	let feature = null
	let featureIndex = null
  const featureIds = featuresList.map(fl => fl.uuid)
	for (let sfi = 0; sfi < selectedFeatures.length; sfi++) {
		featureIndex = featureIds.indexOf(selectedFeatures[sfi])
		if (featureIndex > -1) {
			feature = featuresList[featureIndex]
			switch (feature.properties.ghType) {
				case FEATURE_TYPES.Polyline:
					numLines++
					break
				case FEATURE_TYPES.Polygon:
					numPolygons++
					break
				case FEATURE_TYPES.Tracks:
					numTracks++
					break
				case FEATURE_TYPES.Point:
					numWaypoints++
					break
			}
			features.push(feature)
		}
	}

	return {
		features,
		numLines,
		numPolygons,
		numTracks,
		numWaypoints,
	}
}, actions)(EditStylesFooter)
